import { useCallback, useEffect, useRef, useState } from "react";
import { ActivityIndicator, Platform, Text, View } from "react-native";
import { ScanBarcode } from "lucide-react-native";
import { CameraView, useCameraPermissions } from "expo-camera";
import {
  ActionBanner,
  MoonFormField,
  MoonValueInput,
  PrimaryButton,
  TextActionButton,
} from "@louise/moon-ui";
import { useTranslations } from "@src/packages/i18n";
import {
  useMedicationPackScan,
  type MedicationPackScanResult,
} from "@src/packages/medications/catalog/useMedicationPackScan";
import { canPhotographPackCode } from "@src/packages/medications/catalog/photoCapture";
import {
  MedicationPackPhotoError,
  describeError,
  type PackPhotoFailure,
} from "@src/packages/medications/catalog/useMedicationCatalog";
import { EMBER } from "../../../../onboarding/theme";
import { semanticColors } from "@louise/theme/tokens";

const PACK_BARCODE_TYPES = [
  "ean13",
  "ean8",
  "upc_a",
  "upc_e",
  "code128",
  "datamatrix",
  "qr",
] as const;

// One line per thing that can go wrong, named by what she should do about it.
const OUTCOME_MESSAGE_KEYS = {
  ambiguous: "add_medication.pack_scan.code_has_multiple_matches",
  not_found: "add_medication.pack_scan.code_not_found",
  unreadable: "add_medication.pack_scan.no_code_visible_in_that_photo",
  too_large: "add_medication.pack_scan.that_photo_was_too_large",
  unsupported: "add_medication.pack_scan.that_photo_could_not_be_opened",
  unreachable: "add_medication.pack_scan.could_not_reach_louise_to_read_it",
  failed: "add_medication.pack_scan.could_not_read_code",
} as const;

type Props = {
  countryCode: string | null;
  onResolved: (result: MedicationPackScanResult) => void;
  onSearchInstead: () => void;
};

export function MedicationPackScanPanel({
  countryCode,
  onResolved,
  onSearchInstead,
}: Props) {
  const t = useTranslations();
  const { resolveCode, resolvePhoto } = useMedicationPackScan();
  const [permission, requestPermission] = useCameraPermissions();
  const [typedCode, setTypedCode] = useState("");
  const [busy, setBusy] = useState(false);
  // What the catalogue said, or why the decode never got that far. The two
  // are one state because the banner shows one line either way, and what she
  // should do next is the only thing that differs between them.
  const [outcome, setOutcome] = useState<
    "ambiguous" | "not_found" | PackPhotoFailure | null
  >(null);
  // A failure on a phone cannot be opened in a debugger, so the one chance to
  // learn why a scan failed is the error itself. Development builds only.
  const [failureDetail, setFailureDetail] = useState<string | null>(null);
  const inFlight = useRef(false);
  const active = useRef(true);
  const seenCodes = useRef(new Set<string>());
  const permissionRequested = useRef(false);
  const camera = useRef<CameraView>(null);
  const cameraRequestInFlight = useRef(false);
  const [openingCamera, setOpeningCamera] = useState(true);
  const [manualEntry, setManualEntry] = useState(false);
  const [previewReady, setPreviewReady] = useState(false);
  const [cameraOpened, setCameraOpened] = useState(false);
  const [cameraUnavailable, setCameraUnavailable] = useState(false);
  const native = Platform.OS !== "web";
  // Desktop web has no scanner to offer: `capture` is a hint browsers there
  // ignore, so the control opens a file picker, and a webcam is not a thing
  // held up to a box. Those browsers go straight to the printed code.
  const canPhotograph = native || canPhotographPackCode();
  const cameraReady =
    !!permission?.granted && !cameraUnavailable && cameraOpened;

  const openCamera = useCallback(async () => {
    if (cameraRequestInFlight.current) return;
    cameraRequestInFlight.current = true;
    setOpeningCamera(true);
    setCameraOpened(false);
    setCameraUnavailable(false);
    setPreviewReady(false);
    try {
      const nextPermission = await requestPermission();
      if (!active.current) return;
      if (nextPermission.granted) setCameraOpened(true);
      else setManualEntry(true);
    } catch {
      if (active.current) {
        setCameraUnavailable(true);
        setManualEntry(true);
      }
    } finally {
      cameraRequestInFlight.current = false;
      if (active.current) setOpeningCamera(false);
    }
  }, [requestPermission]);

  useEffect(() => {
    active.current = true;
    return () => {
      active.current = false;
    };
  }, []);

  useEffect(() => {
    // The entry row is the user's camera intent. Start once on both platforms;
    // denied access falls straight back to the printed-code field.
    if (!permissionRequested.current) {
      permissionRequested.current = true;
      if (canPhotograph) void openCamera();
      else {
        setOpeningCamera(false);
        setManualEntry(true);
      }
    }
  }, [openCamera, canPhotograph]);

  const readPack = useCallback(
    async (read: () => Promise<MedicationPackScanResult | null>) => {
      if (inFlight.current) return;
      inFlight.current = true;
      setBusy(true);
      setOutcome(null);
      setFailureDetail(null);
      try {
        const result = await read();
        if (!active.current || !result) return;
        if (
          result.resolution.resolution === "exact" &&
          result.resolution.presentation &&
          result.resolution.medicationId
        ) {
          onResolved(result);
        } else {
          setManualEntry(true);
          setOutcome(
            result.resolution.resolution === "exact"
              ? "failed"
              : result.resolution.resolution,
          );
        }
      } catch (error) {
        if (active.current) {
          setManualEntry(true);
          setOutcome(
            error instanceof MedicationPackPhotoError ? error.reason : "failed",
          );
          setFailureDetail(
            error instanceof MedicationPackPhotoError && error.detail
              ? `${error.stage ?? "request"}: ${error.detail}`
              : describeError(error),
          );
        }
      } finally {
        inFlight.current = false;
        if (active.current) setBusy(false);
      }
    },
    [onResolved],
  );

  return (
    <View testID="medication-pack-scan-panel" style={{ gap: 12 }}>
      {!manualEntry && (cameraReady || openingCamera) ? (
        <View
          testID="medication-pack-camera-preview"
          style={{
            height: 180,
            borderRadius: 12,
            overflow: "hidden",
            backgroundColor: "#000",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {cameraReady ? (
            <CameraView
              ref={camera}
              style={{ width: "100%", height: "100%" }}
              facing="back"
              barcodeScannerSettings={{ barcodeTypes: [...PACK_BARCODE_TYPES] }}
              onCameraReady={() => setPreviewReady(true)}
              onMountError={() => {
                setCameraUnavailable(true);
                setPreviewReady(false);
                setManualEntry(true);
              }}
              onBarcodeScanned={
                native
                  ? ({ data }) => {
                      if (
                        !active.current ||
                        inFlight.current ||
                        seenCodes.current.has(data)
                      ) {
                        return;
                      }
                      seenCodes.current.add(data);
                      setTypedCode(data);
                      void readPack(() => resolveCode(data, countryCode));
                    }
                  : undefined
              }
            />
          ) : (
            <ActivityIndicator color={EMBER.colors.textMid} />
          )}
          {native && busy ? (
            <View
              style={{
                position: "absolute",
                inset: 0,
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <ActivityIndicator color={semanticColors.onDark.text.primary} />
            </View>
          ) : null}
        </View>
      ) : null}
      {cameraUnavailable || permission?.status === "denied" ? (
        <ActionBanner
          testID="medication-pack-camera-unavailable"
          variant="info"
          message={t(
            cameraUnavailable
              ? "add_medication.pack_scan.camera_unavailable"
              : "add_medication.pack_scan.camera_access_denied",
          )}
        />
      ) : null}
      {manualEntry ? (
        <MoonFormField
          label={t("add_medication.pack_scan.printed_code")}
          labelIcon={ScanBarcode}
        >
          <MoonValueInput
            testID="medication-pack-code-input"
            value={typedCode}
            onChangeText={(value) => {
              setTypedCode(value);
              setOutcome(null);
            }}
            accessibilityLabel={t("add_medication.pack_scan.printed_code")}
            placeholder={t("add_medication.pack_scan.example_code")}
            disabled={busy}
            maxLength={500}
            textInputProps={{
              autoCapitalize: "characters",
              autoCorrect: false,
            }}
          />
        </MoonFormField>
      ) : null}
      {outcome ? (
        <ActionBanner
          testID="medication-pack-scan-outcome"
          variant={
            outcome === "ambiguous" || outcome === "not_found"
              ? "info"
              : "important"
          }
          message={t(OUTCOME_MESSAGE_KEYS[outcome])}
        />
      ) : null}
      {__DEV__ && failureDetail ? (
        <Text
          testID="medication-pack-scan-failure-detail"
          selectable
          style={{ fontSize: 11, color: EMBER.colors.textLight }}
        >
          {failureDetail}
        </Text>
      ) : null}
      {manualEntry || !native ? (
        <PrimaryButton
          testID="medication-pack-code-submit"
          label={t(
            manualEntry
              ? "add_medication.pack_scan.look_up_code"
              : "add_medication.pack_scan.scan_pack_code",
          )}
          action="resolve_pack_code"
          loading={busy}
          disabled={
            manualEntry ? !typedCode.trim() : !cameraReady || !previewReady
          }
          onPress={() => {
            void readPack(async () => {
              if (manualEntry) {
                return resolveCode(typedCode.trim(), countryCode);
              }
              if (!camera.current || !previewReady) return null;
              const photo = await camera.current.takePictureAsync({
                quality: 0.8,
              });
              if (!active.current) return null;
              return resolvePhoto(photo.uri, "pack.jpg", countryCode);
            });
          }}
        />
      ) : null}
      {!manualEntry ? (
        <TextActionButton
          label={t("add_medication.pack_scan.enter_code_manually")}
          action="enter_pack_code_manually"
          onPress={() => setManualEntry(true)}
          disabled={busy}
          centered
        />
      ) : null}
      <TextActionButton
        label={t("add_medication.pack_scan.search_by_name_instead")}
        action="search_instead_of_scan"
        onPress={onSearchInstead}
        disabled={busy}
        centered
      />
    </View>
  );
}
